import { useEffect, useCallback, useRef } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { baseProductConfigurationStepId } from '../../utilities/strings';
import { isStepRequired } from '../../utilities/stepValidation';

/**
 * Custom hook for PersonalisationModal logic and className selection.
 * @param {Object} options - Options object.
 * @param {boolean} options.isOpen - Whether the modal is open.
 * @param {function} options.onClose - Callback for closing the modal.
 * @returns {Object} View model for the modal component.
 */
export function useViewModel({ isOpen, onClose }) {
  const {
    personalisationSteps,
    personalisationSlots,
    setCurrentStepId,
    currentStepId,
    isResetting,
    resetJourney,
    isAddingToCart,
    isRetrying
  } = usePersonalisationContext();

  // Handle personalisation modal dialog
  /** @type {React.RefObject<HTMLDialogElement | null>} */
  const dialogModalRef = useRef(null);

  // Handle content scroll position
  /** @type {React.RefObject<HTMLDivElement | null>} */
  const contentRef = useRef(null);

  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
      setTimeout(() => {
        dialog.setAttribute('data-open', 'true');
      }, 10);
    } else if (!isOpen && dialog.open && !isAddingToCart) {
      // Only close if not adding to cart
      dialog.setAttribute('data-open', 'false');
      setTimeout(() => {
        dialog.close();
      }, 400);
    }
  }, [isOpen, isAddingToCart]);

  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog) return;

    const handleNativeClose = () => {
      if (!isOpen) return;
      if (isOpen && !isAddingToCart) {
        // Only close if not adding to cart
        dialog.setAttribute('data-open', 'false');
        setTimeout(() => {
          onClose?.();
        }, 400);
      }
    };

    dialog.addEventListener('close', handleNativeClose);
    return () => {
      dialog.removeEventListener('close', handleNativeClose);
    };
  }, [isOpen, onClose, isAddingToCart]);

  const handleClose = () => {
    if (onClose && !isAddingToCart) {
      // Only allow close if not adding to cart
      onClose();
    }
  };

  // Compute total steps and current step index
  const stepIds = Array.from(personalisationSteps.keys());
  const totalSteps = personalisationSteps?.size || 0;
  const currentStep = stepIds.indexOf(currentStepId);

  // Dynamic header title for modal. Header is configured for each step.
  let headerTitle = 'Add Personalisation';
  if (personalisationSteps && currentStepId) {
    const currentStepObj = personalisationSteps.get(currentStepId);

    // For base config step, use uiStepHeading from metaobject
    if (currentStepId === baseProductConfigurationStepId && currentStepObj?.uiStepHeading) {
      headerTitle = currentStepObj.uiStepHeading;
    } else if (currentStepObj && currentStepObj.uiHeading) {
      // Process the title for optional steps
      const rawHeading = currentStepObj.uiHeading.replace('(optional)', '').trim();

      // Check if step is completely optional (not required)
      const stepIsRequired = isStepRequired({
        step: currentStepObj,
        personalisationSlots: personalisationSlots || new Map()
      });

      const isCompletelyOptional = !stepIsRequired;

      headerTitle = isCompletelyOptional ? `${rawHeading} (optional)` : rawHeading;
    }
  }

  const headerResetText = 'Reset';

  /**
   * Scrolls the content area to the top.
   */
  const scrollToTop = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo(0, 0);
    }
  }, []);

  const handleNext = useCallback(() => {
    setCurrentStepId((prevId) => {
      const ids = Array.from(personalisationSteps.keys());
      const currentIndex = ids.indexOf(prevId);
      const nextId = ids.at(currentIndex + 1);
      return nextId || prevId;
    });
    // Scroll to top when navigating to next step.
    scrollToTop();
  }, [personalisationSteps, setCurrentStepId, scrollToTop]);

  const handlePrev = useCallback(() => {
    setCurrentStepId((prevId) => {
      const ids = Array.from(personalisationSteps.keys());
      const currentIndex = ids.indexOf(prevId);
      const newPrevId = ids.at(currentIndex - 1);
      return newPrevId || prevId;
    });
    // Scroll to top when navigating to previous step.
    scrollToTop();
  }, [personalisationSteps, setCurrentStepId, scrollToTop]);

  return {
    dialogModalRef,
    contentRef,
    totalSteps,
    currentStep,
    handleClose,
    handleNext,
    handlePrev,
    handleReset: resetJourney,
    isResetting,
    isAddingToCart,
    isRetrying,
    headerTitle,
    headerResetText
  };
}
